import { Button, Checkbox, ColorPicker, Divider, Form, Input, message, Modal, Tag, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { ApiService } from "api/api.service";
import { OrganisationDto, PageType, SocialType } from "dtos/organisation.dto";
import { useEffect, useState } from "react";
import {
  TbBrandFacebook,
  TbBrandInstagram,
  TbBrandLinkedin,
  TbBrandTiktok,
  TbBrandX,
  TbBrandYoutube,
  TbFileDescription,
  TbPalette,
  TbShare,
  TbShieldCheck,
} from "react-icons/tb";
import { PolicySeverity } from "dtos/smart-negotiation.dto";

const { Text } = Typography;

// ── Types ────────────────────────────────────────────────────────────────────

type ThemingModalProps = {
  open: boolean;
  onClose: () => void;
  slug: string;
  onSuccess?: () => void;
};

type StepKey = "theme" | "socials" | "pages" | "policies";

// ── Constants ────────────────────────────────────────────────────────────────

const STEPS: { key: StepKey; label: string; description: string; icon: React.ReactNode }[] = [
  {
    key: "theme",
    label: "Theme",
    description: "Brand colors, logo, and company details",
    icon: <TbPalette size={20} />,
  },
  {
    key: "socials",
    label: "Socials",
    description: "Social media links shown in emails",
    icon: <TbShare size={20} />,
  },
  {
    key: "pages",
    label: "Pages",
    description: "Legal and policy page links",
    icon: <TbFileDescription size={20} />,
  },
  {
    key: "policies",
    label: "Policies",
    description: "Email compliance and tone policies",
    icon: <TbShieldCheck size={20} />,
  },
];

const SOCIAL_FIELDS: { type: SocialType; label: string; icon: React.ReactNode; placeholder: string }[] = [
  { type: "facebook", label: "Facebook", icon: <TbBrandFacebook />, placeholder: "https://facebook.com/yourpage" },
  { type: "instagram", label: "Instagram", icon: <TbBrandInstagram />, placeholder: "https://instagram.com/yourpage" },
  { type: "twitter", label: "Twitter / X", icon: <TbBrandX />, placeholder: "https://x.com/yourhandle" },
  {
    type: "linkedin",
    label: "LinkedIn",
    icon: <TbBrandLinkedin />,
    placeholder: "https://linkedin.com/company/yourpage",
  },
  { type: "youtube", label: "YouTube", icon: <TbBrandYoutube />, placeholder: "https://youtube.com/@yourchannel" },
  { type: "tiktok", label: "TikTok", icon: <TbBrandTiktok />, placeholder: "https://tiktok.com/@yourhandle" },
];

const PAGE_FIELDS: { type: PageType; label: string; placeholder: string }[] = [
  { type: "privacy_policy", label: "Privacy Policy", placeholder: "https://yoursite.com/privacy" },
  { type: "terms_of_service", label: "Terms of Service", placeholder: "https://yoursite.com/terms" },
  { type: "cookie_policy", label: "Cookie Policy", placeholder: "https://yoursite.com/cookies" },
  { type: "disclaimer", label: "Disclaimer", placeholder: "https://yoursite.com/disclaimer" },
  { type: "refund_policy", label: "Refund Policy", placeholder: "https://yoursite.com/refund-policy" },
  { type: "accessibility", label: "Accessibility", placeholder: "https://yoursite.com/accessibility" },
];

// ── Policies: Hardcoded data + query ─────────────────────────────────────────

const SEVERITY_CONFIG: Record<PolicySeverity, { color: string; label: string }> = {
  block: { color: "red", label: "Block" },
  critical: { color: "orange", label: "Critical" },
  warn: { color: "gold", label: "Warn" },
  suggest: { color: "blue", label: "Suggest" },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function socialsArrayToMap(socials?: Array<{ type: SocialType; url: string }> | null): Record<SocialType, string> {
  const map: Record<SocialType, string> = {
    facebook: "",
    instagram: "",
    twitter: "",
    linkedin: "",
    youtube: "",
    tiktok: "",
  };
  socials?.forEach((s) => {
    if (s.type in map) map[s.type] = s.url;
  });
  return map;
}

function pagesArrayToMap(pages?: Array<{ type: PageType; url: string }> | null): Record<PageType, string> {
  const map: Record<PageType, string> = {
    privacy_policy: "",
    terms_of_service: "",
    cookie_policy: "",
    disclaimer: "",
    refund_policy: "",
    accessibility: "",
  };
  pages?.forEach((p) => {
    if (p.type in map) map[p.type] = p.url;
  });
  return map;
}

function socialsMapToArray(map: Record<SocialType, string>): Array<{ type: SocialType; url: string }> {
  return Object.entries(map)
    .filter(([, url]) => url?.trim())
    .map(([type, url]) => ({ type: type as SocialType, url: url.trim() }));
}

function pagesMapToArray(map: Record<PageType, string>): Array<{ type: PageType; url: string }> {
  return Object.entries(map)
    .filter(([, url]) => url?.trim())
    .map(([type, url]) => ({ type: type as PageType, url: url.trim() }));
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StepNav({ activeStep, onStepChange }: { activeStep: StepKey; onStepChange: (step: StepKey) => void }) {
  return (
    <div className="flex flex-col gap-1">
      {STEPS.map((step) => {
        const isActive = activeStep === step.key;
        return (
          <div
            key={step.key}
            onClick={() => onStepChange(step.key)}
            className={`flex items-start gap-3 rounded-lg px-3 py-3 cursor-pointer transition-colors ${
              isActive ? "bg-purple-100" : "hover:bg-purple-50"
            }`}
          >
            <div
              className={`flex items-center justify-center size-9 rounded-lg shrink-0 ${
                isActive ? "bg-white shadow-sm border border-gray-200 text-purple-600" : "bg-gray-50 text-gray-400"
              }`}
            >
              {step.icon}
            </div>
            <div className="min-w-0">
              <Text className={`block text-[13px] leading-5 ${isActive ? "!font-semibold" : "!font-medium"}`}>
                {step.label}
              </Text>
              <Text type="secondary" className="block text-xs leading-4">
                {step.description}
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ThemeForm({
  organisation,
  onSave,
  loading,
}: {
  organisation: OrganisationDto;
  onSave: (values: Partial<OrganisationDto["meta"]>) => void;
  loading: boolean;
}) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!organisation?.meta) return;
    form.setFieldsValue({
      company_name: organisation.meta.company_name,
      title: organisation.meta.title,
      tagline: organisation.meta.tagline,
      website: organisation.meta.website,
      logo: organisation.meta.logo,
      favicon: organisation.meta.favicon,
      primary: organisation.meta.color?.primary,
      secondary: organisation.meta.color?.secondary,
    });
  }, [organisation, form]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => {
        onSave({
          company_name: values.company_name,
          title: values.title,
          tagline: values.tagline,
          website: values.website,
          logo: values.logo,
          favicon: values.favicon,
          color: {
            ...organisation?.meta?.color,
            primary: values.primary,
            secondary: values.secondary,
          },
        });
      }}
    >
      <Text className="block text-[15px] !font-semibold mb-1">Theme</Text>
      <Text type="secondary" className="block text-xs mb-4">
        Customize the branding that appears in email templates.
      </Text>
      <Divider className="my-4" />

      <Form.Item name="company_name" label="Company Name">
        <Input placeholder="First Choice Debt Solutions" />
      </Form.Item>

      <Form.Item name="title" label="Title">
        <Input placeholder="Page title shown in browser tab" />
      </Form.Item>

      <Form.Item name="tagline" label="Tagline">
        <Input placeholder="Your path to financial freedom" />
      </Form.Item>

      <Form.Item name="website" label="Website" rules={[{ type: "url", message: "Must be a valid URL" }]}>
        <Input placeholder="https://yourcompany.com" />
      </Form.Item>

      <Form.Item name="logo" label="Logo URL" rules={[{ type: "url", message: "Must be a valid URL" }]}>
        <Input placeholder="https://yourcompany.com/logo.png" />
      </Form.Item>

      <Form.Item name="favicon" label="Favicon URL" rules={[{ type: "url", message: "Must be a valid URL" }]}>
        <Input placeholder="https://yourcompany.com/favicon.ico" />
      </Form.Item>

      <Divider className="my-4" />

      <div className="grid grid-cols-2 gap-4">
        <Form.Item name="primary" label="Primary Color">
          <ColorInput />
        </Form.Item>
        <Form.Item name="secondary" label="Secondary Color">
          <ColorInput />
        </Form.Item>
      </div>

      <Form.Item className="mb-0">
        <Button type="primary" htmlType="submit" loading={loading}>
          Save Theme
        </Button>
      </Form.Item>
    </Form>
  );
}

function ColorInput({ value, onChange }: { value?: string; onChange?: (val: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <ColorPicker value={value || undefined} onChange={(_, hex) => onChange?.(hex)} showText />
      <Input
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder="#6f4fef"
        className="flex-1"
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}
      />
    </div>
  );
}

function SocialsForm({
  organisation,
  onSave,
  loading,
}: {
  organisation: OrganisationDto;
  onSave: (socials: Array<{ type: SocialType; url: string }>) => void;
  loading: boolean;
}) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!organisation?.meta) return;
    form.setFieldsValue(socialsArrayToMap(organisation.meta.socials as any));
  }, [organisation, form]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => {
        onSave(socialsMapToArray(values as Record<SocialType, string>));
      }}
    >
      <Text className="block text-[15px] !font-semibold mb-1">Social Media</Text>
      <Text type="secondary" className="block text-xs mb-4">
        Add links to show social media icons in email footers. Leave blank to hide.
      </Text>
      <Divider className="my-4" />

      {SOCIAL_FIELDS.map((field) => (
        <Form.Item
          key={field.type}
          name={field.type}
          label={
            <span className="inline-flex items-center gap-2">
              {field.icon}
              {field.label}
            </span>
          }
          rules={[{ type: "url", message: "Must be a valid URL", warningOnly: true }]}
        >
          <Input placeholder={field.placeholder} allowClear />
        </Form.Item>
      ))}

      <Form.Item className="mb-0">
        <Button type="primary" htmlType="submit" loading={loading}>
          Save Socials
        </Button>
      </Form.Item>
    </Form>
  );
}

function PagesForm({
  organisation,
  onSave,
  loading,
}: {
  organisation: OrganisationDto;
  onSave: (pages: Array<{ type: PageType; url: string }>) => void;
  loading: boolean;
}) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!organisation?.meta) return;
    form.setFieldsValue(pagesArrayToMap(organisation.meta.pages as any));
  }, [organisation, form]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => {
        onSave(pagesMapToArray(values as Record<PageType, string>));
      }}
    >
      <Text className="block text-[15px] !font-semibold mb-1">Legal Pages</Text>
      <Text type="secondary" className="block text-xs mb-4">
        Add URLs to show footer links in email templates. Leave blank to hide.
      </Text>
      <Divider className="my-4" />

      {PAGE_FIELDS.map((field) => (
        <Form.Item
          key={field.type}
          name={field.type}
          label={field.label}
          rules={[{ type: "url", message: "Must be a valid URL", warningOnly: true }]}
        >
          <Input placeholder={field.placeholder} allowClear />
        </Form.Item>
      ))}

      <Form.Item className="mb-0">
        <Button type="primary" htmlType="submit" loading={loading}>
          Save Pages
        </Button>
      </Form.Item>
    </Form>
  );
}

function PoliciesForm({
  onSave,
  loading,
  slug,
}: {
  onSave: (selectedKeys: string[]) => void;
  loading: boolean;
  slug: string;
}) {
  const { data, isLoading } = ApiService.negotiation.usePolicies();
  const getOrgPoliciesRes = ApiService.negotiation.useOrgPolicies(slug);
  const orgPolicies = getOrgPoliciesRes.data?.result;
  const policies = data?.result ?? [];

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const allSelected = policies.length > 0 && selected.size === policies.length;
  const someSelected = selected.size > 0 && selected.size < policies.length;

  useEffect(() => {
    setSelected(new Set((orgPolicies ?? [])?.map((policies) => policies.key)));
  }, [orgPolicies]);

  const handleSelectAll = (checked: boolean) => {
    setSelected(checked ? new Set(policies.map((p) => p.key)) : new Set());
  };

  const handleTogglePolicy = (key: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      checked ? next.add(key) : next.delete(key);
      return next;
    });
  };

  const handleToggleExpand = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <div>
      <Text className="block text-[15px] !font-semibold mb-1">Policies</Text>
      <Text type="secondary" className="block text-xs mb-4">
        Select which compliance policies to enforce for this {`organisation's`} emails.
      </Text>
      <Divider className="my-4" />

      {/* Select All row */}
      <div className="flex items-center justify-between mb-3">
        <Checkbox
          indeterminate={someSelected}
          checked={allSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        >
          <Text className="!text-[13px] !font-medium">Select All</Text>
        </Checkbox>

        {selected.size > 0 && (
          <Text type="secondary" className="text-xs">
            {selected.size} of {policies.length} selected
          </Text>
        )}
      </div>

      {/* Policy list */}
      <div className="flex flex-col gap-2">
        {isLoading || getOrgPoliciesRes.isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 rounded-lg bg-gray-100 animate-pulse" />
            ))
          : policies.map((policy) => {
              const isChecked = selected.has(policy.key);
              const isExpanded = expanded.has(policy.key);
              const severity = SEVERITY_CONFIG[policy.severity];

              return (
                <div
                  key={policy.key}
                  onClick={() => handleTogglePolicy(policy.key, !isChecked)}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isChecked
                      ? "border-purple-200 bg-purple-50"
                      : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  <Checkbox
                    checked={isChecked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleTogglePolicy(policy.key, e.target.checked)}
                    className="mt-0.5 shrink-0"
                  />

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Text className="!text-[13px] !font-medium leading-5">{policy.name}</Text>
                      <Tag color={severity.color} className="!text-[11px] !leading-4 !px-1.5 !py-0 !m-0 capitalize">
                        {severity.label}
                      </Tag>
                    </div>

                    <div className="mt-0.5">
                      <Text type="secondary" className="text-xs leading-5 block">
                        {policy.description}
                      </Text>
                    </div>
                  </div>
                </div>
              );
            })}
      </div>

      {/* Save button */}
      <div className="mt-4">
        <Button type="primary" loading={loading} onClick={() => onSave(Array.from(selected))}>
          Save Policies
        </Button>
      </div>
    </div>
  );
}

function ThemingModal({ open, onClose, slug, onSuccess }: ThemingModalProps) {
  const [activeStep, setActiveStep] = useState<StepKey>("theme");
  const organisation = ApiService.organisation.useGetBySlug(open ? slug : undefined);
  const updateOrganisation = ApiService.organisation.useUpdateOrganisation();
  const savePolicies = ApiService.negotiation.useSavePolicies();

  // TODO: wire up a real save-policies mutation when the API is ready.
  // For now this is a stub — replace `savePoliciesLoading` with the mutation's isPending.
  const [savePoliciesLoading, setSavePoliciesLoading] = useState(false);

  // Reset to first step when opening
  useEffect(() => {
    if (open) setActiveStep("theme");
  }, [open]);

  const handleSaveTheme = (meta: Partial<OrganisationDto["meta"]>) => {
    updateOrganisation.mutate(
      //TO-DO
      { slug, body: { meta: { ...organisation.data?.meta, ...meta } as any } },
      {
        onSuccess: () => {
          message.success("Theme updated");
          organisation.refetch();
          onSuccess?.();
        },
      },
    );
  };

  const handleSaveSocials = (socials: Array<{ type: SocialType; url: string }>) => {
    updateOrganisation.mutate(
      //TO-DO
      { slug, body: { meta: { ...organisation.data?.meta, socials } as any } },
      {
        onSuccess: () => {
          message.success("Social links updated");
          organisation.refetch();
          onSuccess?.();
        },
      },
    );
  };

  const handleSavePages = (pages: Array<{ type: PageType; url: string }>) => {
    updateOrganisation.mutate(
      { slug, body: { meta: { ...organisation.data?.meta, pages } as any } },
      {
        onSuccess: () => {
          message.success("Page links updated");
          organisation.refetch();
          onClose();
        },
      },
    );
  };

  const handleSavePolicies = (selectedKeys: string[]) => {
    savePolicies.mutate(
      { body: { org_code: slug, rule_keys: selectedKeys } },
      {
        onSuccess: () => {
          message.success("Policies updated");
          onSuccess?.();
        },
      },
    );
  };

  const orgData = organisation.data;

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      title={null}
      closable
      destroyOnClose
      styles={{
        body: { padding: 0 },
        content: { padding: 0, overflow: "hidden" },
      }}
    >
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-gray-100">
        <Text className="block text-[17px] !font-semibold">Organisation Configuration</Text>
        <Text type="secondary" className="block text-xs mt-0.5">
          Customize how emails look for this organisation.
        </Text>
      </div>

      <div className="flex" style={{ minHeight: 520 }}>
        {/* Left sidebar */}
        <div className="w-[320px] shrink-0 border-r border-gray-100 p-3">
          <StepNav activeStep={activeStep} onStepChange={setActiveStep} />
        </div>

        {/* Right content */}
        <div className="flex-1 p-6 overflow-y-auto" style={{ maxHeight: 520 }}>
          {orgData && activeStep === "theme" && (
            <ThemeForm organisation={orgData} onSave={handleSaveTheme} loading={updateOrganisation.isPending} />
          )}

          {orgData && activeStep === "socials" && (
            <SocialsForm organisation={orgData} onSave={handleSaveSocials} loading={updateOrganisation.isPending} />
          )}

          {orgData && activeStep === "pages" && (
            <PagesForm organisation={orgData} onSave={handleSavePages} loading={updateOrganisation.isPending} />
          )}

          {/* Policies does not depend on orgData — it has its own query */}
          {activeStep === "policies" && (
            <PoliciesForm onSave={handleSavePolicies} loading={savePolicies.isPending} slug={slug} />
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ThemingModal;
